/**
 * Dark Theme - Enterprise SaaS
 *
 * Graphite work surfaces matching the light theme's blue, teal, and amber
 * hierarchy.
 */
// Text tones the sidebar shares with the main ladder, declared once so raising
// one raises both. These were copied hex values, and a contrast fix to the
// ladder left the sidebar behind without anything failing.
const TEXT_HEADING = '#F8FAFC';
const TEXT_NEUTRAL = '#94A3B8';
const TEXT_SECONDARY = '#CBD5E1';

const darkTheme = {
  name: 'dark',
  label: 'Dark',
  mode: 'dark',
  colorSet: 'default',

  colors: {
    // Background elevation system
    canvas: '#0F1115',
    panelPrimary: '#171A21',
    panelSecondary: '#1F2430',
    panelTertiary: '#252B36',
    panelQuaternary: '#2B3340',
    surfaceHover: '#323B49',

    // Borders
    borderSubtle: '#334155',
    borderStrong: '#475569',

    // Text hierarchy
    textHeading: TEXT_HEADING,
    textPrimary: '#E5E7EB',
    textSecondary: TEXT_SECONDARY,
    textSupporting: '#AAB6C5',
    textNeutral: TEXT_NEUTRAL,
    textMuted: '#8994A4',
    textInverse: '#0F1115',

    // Primary accent: enterprise blue
    accentPrimary: '#8AB4FF',
    accentPrimaryVibrant: '#B6CCFF',

    // Secondary accent: analytical teal
    accentSecondary: '#5DD6C7',
    accentSecondaryVibrant: '#8BE7DC',

    // Warm accent: CTA and warning amber
    accentWarm: '#F2B15C',
    accentWarmVibrant: '#FFD08A',

    // Status colors
    statusDanger: '#FF8A80',
    statusWarning: '#FBBF6B',
    statusSuccess: '#74D99F',
    statusInfo: '#8AB4FF',

    // Support colors
    gridline: 'rgba(148, 163, 184, 0.16)',
    focusOutline: 'rgba(138, 180, 255, 0.65)',
    overlay: 'rgba(5, 7, 10, 0.78)',

    // Component-specific
    headerBg: '#141821',
    sidebarBg: '#101318',
    cardBg: '#171A21',
    inputBg: '#202631',
    dropdownBg: '#171A21',

    // Navigation sidebar
    navSectionHeader: TEXT_NEUTRAL,
    navItemText: TEXT_SECONDARY,
    navItemHover: TEXT_HEADING,
    navItemHoverBg: 'rgba(138, 180, 255, 0.10)',
    navItemActive: '#8AB4FF',
    navItemActiveBg: 'rgba(138, 180, 255, 0.16)',
    navIconDefault: TEXT_NEUTRAL,
    navIconHover: '#8AB4FF',
    navIconActive: '#8AB4FF',
    navDivider: 'rgba(148, 163, 184, 0.20)',

    // Table cell colors
    tableEntity: '#E2E8F0',
    tableMetric: '#9FD2CA',
    tableBody: '#C3CEDA',
    tableSecondary: '#96A3B4',
    tableTime: '#8793A3',

    // Quality distribution colors
    qualityP20: '#FF8A80',
    qualityP40: '#FFB274',
    qualityP60: '#F6D878',
    qualityP80: '#9BE27D',
    qualityP100: '#74D99F',

    // Pill styling - elevated backgrounds
    pillBgFrom: '#1F2430',
    pillBgTo: '#252B36',
    pillActiveBgFrom: '#253B59',
    pillActiveBgTo: '#2D4A70',
    pillHoverBgFrom: '#252B36',
    pillHoverBgTo: '#303847',

    // Table-specific
    tableHeaderBg: '#202631',
    tableRowHover: 'rgba(138, 180, 255, 0.09)',
    tableRowExpanded: 'rgba(93, 214, 199, 0.08)',
    buttonTextOnAccent: '#0F1115',

    // RGB channels for opacity modifiers
    statusDangerRgb: '255, 138, 128',
    statusSuccessRgb: '116, 217, 159',
    statusWarningRgb: '251, 191, 107',
    statusInfoRgb: '138, 180, 255',
    accentWarmRgb: '242, 177, 92',

    // Severity
    severityCriticalText: '#FF8A80',
    severityCriticalBg: 'rgba(255, 138, 128, 0.13)',
    severityCriticalBorder: 'rgba(255, 138, 128, 0.34)',
    severityCriticalGlow: '0 0 18px rgba(255, 138, 128, 0.28)',
    severityHighText: '#FBBF6B',
    severityHighBg: 'rgba(251, 191, 107, 0.13)',
    severityHighBorder: 'rgba(251, 191, 107, 0.34)',
    severityHighGlow: '0 0 18px rgba(251, 191, 107, 0.28)',
    severityMediumText: '#F2B15C',
    severityMediumBg: 'rgba(242, 177, 92, 0.13)',
    severityMediumBorder: 'rgba(242, 177, 92, 0.34)',
    severityMediumGlow: '0 0 18px rgba(242, 177, 92, 0.28)',
    severityLowText: '#8AB4FF',
    severityLowBg: 'rgba(138, 180, 255, 0.13)',
    severityLowBorder: 'rgba(138, 180, 255, 0.34)',
    severityLowGlow: '0 0 18px rgba(138, 180, 255, 0.28)',
    severitySuccessText: '#74D99F',
    severitySuccessBg: 'rgba(116, 217, 159, 0.13)',
    severitySuccessBorder: 'rgba(116, 217, 159, 0.34)',
    severitySuccessGlow: '0 0 8px rgba(116, 217, 159, 0.28)',

    // Border hierarchy
    borderMuted: '#334155',
    borderMedium: '#475569',
    borderAccent: '#64748B',

    // Surface overlays - subtle layering
    surfaceSubtle: 'rgba(226, 232, 240, 0.045)',
    surfaceMid: 'rgba(226, 232, 240, 0.075)',
    surfaceOverlayHover: 'rgba(138, 180, 255, 0.075)',

    // Scrollbar
    scrollbarTrack: 'rgba(51, 65, 85, 0.42)',
    scrollbarThumb: 'rgba(148, 163, 184, 0.48)',
    scrollbarThumbHover: 'rgba(203, 213, 225, 0.62)',

    // Selection
    selectionBg: 'rgba(138, 180, 255, 0.30)',

    // Graph visualization
    graphBg: '#101318',
    graphNodeBg: '#1F2430',
    graphNodeText: '#E5E7EB',
  },

  shadows: {
    card: '0 1px 2px rgba(0, 0, 0, 0.36), 0 12px 28px rgba(0, 0, 0, 0.42)',
    soft: '0 4px 16px rgba(0, 0, 0, 0.34), 0 18px 40px rgba(0, 0, 0, 0.38)',
    dropdown: '0 12px 32px rgba(0, 0, 0, 0.50), 0 1px 3px rgba(0, 0, 0, 0.42)',
    drawer: '-8px 0 24px rgba(0, 0, 0, 0.44), -1px 0 3px rgba(0, 0, 0, 0.36)',
  },
};

export default darkTheme;
